import fs from 'fs/promises';
import path from 'path';
import { fileURLToPath } from 'url';
import { parse } from '@babel/parser';
import type {
  File as BabelFile,
  JSXIdentifier,
  JSXMemberExpression,
  JSXOpeningElement,
} from '@babel/types';
import MagicString from 'magic-string';
import type { Plugin } from 'vite';

import {
  DATA_ATTRIBUTES,
  R3F_BAILOUT_ELEMENTS,
  R3F_BAILOUT_PATTERNS,
  R3F_IMPORT_SOURCES,
} from './constants';

const validExtensions = new Set(['.jsx', '.tsx']);

/**
 * Normalize a file path to use forward slashes.
 * Vite normalizes all module IDs to forward-slash form (even on Windows),
 * so every path that will be compared to a Vite `id` must be normalized the
 * same way.  We explicitly replace backslashes rather than relying on
 * `path.sep` so the behaviour is correct even when the path string contains
 * Windows separators on a non-Windows host (e.g. in tests).
 */
function normalizePath(p: string): string {
  return p.replace(/\\/g, '/');
}

/** Tailwind config file names to search for in the project root, in priority order */
const TAILWIND_CONFIG_FILES = [
  'tailwind.config.ts',
  'tailwind.config.js',
  'tailwind.config.mjs',
  'tailwind.config.cjs',
];

// Keep in sync with versions used in stack templates
const TAILWIND_V4_CDN_URL =
  'https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4.1.14';
const TAILWIND_V3_CDN_URL = 'https://cdn.tailwindcss.com/3.4.17';

export interface CartographerOptions {
  /** Override the root directory for metadata path resolution (absolute or relative to Vite root) */
  root?: string;
}

export function cartographer(options?: CartographerOptions): Plugin {
  let clientScript: string;
  let viteRoot: string;
  let configuredRoot: string;
  let configuredRootName: string;
  let tailwindConfigPath: string | null = null;
  let tailwindMajorVersion: number = 4;
  /** The file ID that was selected for tailwind config injection (null = not yet chosen). */
  let tailwindInjectionTarget: string | null = null;

  return {
    name: '@replit/vite-plugin-cartographer',
    enforce: 'pre' as const,

    async configResolved(config) {
      // Vite normalizes config.root to forward slashes on all platforms.
      // We normalize every stored path the same way so that comparisons
      // against Vite module IDs (which always use forward slashes) work
      // correctly on Windows.
      viteRoot = normalizePath(config.root);

      if (options?.root) {
        configuredRoot = normalizePath(
          path.isAbsolute(options.root)
            ? options.root
            : path.resolve(config.root, options.root),
        );
      } else {
        configuredRoot = viteRoot;
      }

      // The name of the directory that contains the configured root
      configuredRootName = path.basename(configuredRoot);

      // Resolve the client script path in both CJS and ESM environments
      const currentFileUrl =
        typeof __dirname === 'string'
          ? path.join(__dirname, '../dist/beacon/index.global.js')
          : fileURLToPath(
              new URL('../dist/beacon/index.global.js', import.meta.url),
            );

      try {
        clientScript = await fs.readFile(currentFileUrl, 'utf-8');
      } catch (error) {
        // eslint-disable-next-line no-console -- This is an error in the plugin
        console.error(
          '[replit-cartographer] Failed to load client script:',
          error,
        );
      }

      // Walk up from the configured root to find the closest package.json.
      // The directory containing package.json is used as the project root for
      // both tailwind config lookup and version detection. This handles cases
      // where the Vite/cartographer root is a subdirectory (e.g. `client/`)
      // but the package.json and tailwind config live in the repository root.
      const packageDir = await findClosestPackageDir(configuredRoot);
      const tailwindSearchRoot = packageDir ?? configuredRoot;

      const rawTailwindConfigPath =
        await findTailwindConfigPath(tailwindSearchRoot);
      tailwindConfigPath = rawTailwindConfigPath
        ? normalizePath(rawTailwindConfigPath)
        : null;
      tailwindMajorVersion =
        await detectTailwindMajorVersion(tailwindSearchRoot);
    },

    resolveId(_source, _importer) {
      // Let Vite handle the resolution
      return null;
    },

    transform: {
      order: 'pre',
      async handler(code: string, id: string) {
        // One day the JS ecosystem will decide on one standard to use, until then
        // We need to dynamically import this so that we don't mess with vite resolving this module
        // Handle CommonJS/ESM interop: @babel/traverse may be wrapped differently depending on the module system
        const traverseModule = await import('@babel/traverse');
        const traverse =
          (
            traverseModule.default as {
              default?: typeof traverseModule.default;
            }
          )?.default ||
          traverseModule.default ||
          traverseModule;

        // Make the tailwind config browser-safe by converting CJS
        // `require()` calls to ESM imports so Vite can bundle them.
        // Plugins are kept as-is — if a plugin is Node-only and throws
        // during initialization, the error is caught at the import site
        // in the entry file (see below) and we skip the config entirely.
        if (tailwindConfigPath && id === tailwindConfigPath) {
          if (typeof traverse !== 'function') {
            return null;
          }

          return makeTailwindConfigBrowserSafe(code, traverse);
        }

        if (
          !validExtensions.has(path.extname(id)) ||
          id.includes('node_modules')
        ) {
          return null;
        }

        try {
          const ast = parse(code, {
            sourceType: 'module',
            plugins: ['jsx', 'typescript'],
          });

          let isR3FFile = usesReactThreeFiber(ast);

          if (isR3FFile) {
            return null;
          }

          const magicString = new MagicString(code);

          // Inject the tailwind config import into the entry file.
          //
          // On the first eligible .tsx/.jsx file under viteRoot we lock in its
          // id as the injection target. Every subsequent transform of that same
          // file (including HMR re-transforms) gets the import prepended again,
          // which keeps `window.REPLIT_APP_TAILWIND_CONFIG` alive across edits.
          //
          // Files outside viteRoot (e.g. in node_modules) are excluded by the
          // check above. Files are eligible if they live anywhere under (or at)
          // viteRoot — this covers both `App.tsx` at root and `src/main.tsx`.
          //
          // We prepend the import optimistically here but only commit the
          // target (tailwindInjectionTarget = id) after all bail-out points
          // have passed, so that an R3F file doesn't permanently lock the
          // target while returning null.
          let didInjectTailwind = false;

          if (tailwindConfigPath) {
            const isInjectionCandidate =
              tailwindInjectionTarget === id ||
              (tailwindInjectionTarget === null &&
                id.startsWith(viteRoot + '/'));

            if (isInjectionCandidate) {
              const relativeConfigPath = normalizePath(
                path.relative(path.dirname(id), tailwindConfigPath),
              );
              const importPath = relativeConfigPath.startsWith('.')
                ? relativeConfigPath
                : `./${relativeConfigPath}`;

              magicString.prepend(
                [
                  `(async () => {`,
                  `  try {`,
                  `    const __replit_tw_mod = await import(${JSON.stringify(importPath)});`,
                  `    const __replit_tw_raw = __replit_tw_mod.default ?? __replit_tw_mod;`,
                  `    if (typeof window !== 'undefined' && __replit_tw_raw && typeof __replit_tw_raw === 'object') {`,
                  `      window.REPLIT_APP_TAILWIND_CONFIG = __replit_tw_raw;`,
                  `      window.dispatchEvent(new CustomEvent('replit-tailwind-config-ready'));`,
                  `    }`,
                  `  } catch (e) {`,
                  `    console.warn('[replit-cartographer] Skipped loading tailwind config due to an incompatible Node plugin:', e);`,
                  `  }`,
                  `})();\n`,
                ].join('\n'),
              );
              didInjectTailwind = true;
            }
          }

          let currentElement: import('@babel/types').Node | null = null;

          if (typeof traverse !== 'function') {
            // eslint-disable-next-line no-console -- I want my errors to be logged :D
            console.error(
              `[replit-cartographer] @babel/traverse did not resolve to a function.`,
            );

            return null; // Skip transformation instead of crashing
          }

          traverse(ast, {
            JSXElement: {
              enter(elementPath) {
                if (isR3FFile) {
                  return; // Optimization: Stop processing if we already know it's R3F
                }

                currentElement = elementPath.node;
              },
              exit() {
                currentElement = null;
              },
            },
            JSXOpeningElement(elementPath) {
              if (isR3FFile) {
                return;
              }

              const jsxNode = elementPath.node;
              const elementName = getElementName(jsxNode);

              if (!elementName) {
                return;
              }

              // Check if this file contains R3F elements that should trigger a file-level bailout
              if (shouldBailout(elementName)) {
                isR3FFile = true;
                elementPath.stop(); // Stop traversing this file entirely

                return;
              }

              // Skip instrumentation for specific ambiguous elements (like 'line')
              // We don't instrument them, but they don't trigger a file-level bailout
              if (elementName === 'line') {
                return;
              }

              if (currentElement) {
                const { line = 0, column: col = 0 } = jsxNode.loc?.start ?? {};

                const relativeToConfigured = path.relative(configuredRoot, id);
                const componentPath = normalizePath(
                  path.join(configuredRootName, relativeToConfigured),
                );

                const componentMetadata =
                  col === 0
                    ? `${componentPath}:${line}`
                    : `${componentPath}:${line}:${col}`;

                magicString.appendLeft(
                  jsxNode.name.end ?? 0,
                  ` ${DATA_ATTRIBUTES.METADATA}="${componentMetadata}" ${DATA_ATTRIBUTES.COMPONENT_NAME}="${elementName}"`,
                );
              }
            },
          });

          if (isR3FFile) {
            return null;
          }

          // Lock in the injection target now that we know the transform will
          // succeed.  If we committed earlier, an R3F bail-out above would
          // have permanently locked the target while returning null, and no
          // other file would ever receive the injection.
          if (didInjectTailwind) {
            tailwindInjectionTarget = id;
          }

          return {
            code: magicString.toString(),
            map: magicString.generateMap({ hires: true }),
          };
        } catch (error) {
          // eslint-disable-next-line no-console -- I want my errors to be logged :D
          console.error(`[replit-cartographer] Error processing ${id}:`, error);

          return null;
        }
      },
    },

    transformIndexHtml() {
      if (!clientScript) {
        return [];
      }

      const tags: Array<{
        tag: string;
        attrs: Record<string, string>;
        children?: string;
        injectTo: 'head' | 'body';
      }> = [
        {
          tag: 'script',
          attrs: { type: 'module' },
          children: clientScript,
          injectTo: 'head',
        },
        {
          tag: 'script',
          attrs: {},
          children: generateClientSideTailwindScript(tailwindMajorVersion),
          injectTo: 'head',
        },
      ];

      return tags;
    },
  };
}

// Helper function to extract element name from JSX node
function getElementName(jsxNode: JSXOpeningElement): string | null {
  if (jsxNode.name.type === 'JSXIdentifier') {
    return jsxNode.name.name;
  }

  if (jsxNode.name.type === 'JSXMemberExpression') {
    const memberExpr = jsxNode.name as JSXMemberExpression;
    const object = memberExpr.object as JSXIdentifier;
    const property = memberExpr.property as JSXIdentifier;

    return `${object.name}.${property.name}`;
  }

  return null;
}

function usesReactThreeFiber(ast: BabelFile): boolean {
  return ast.program.body.some(
    (node) =>
      node.type === 'ImportDeclaration' &&
      typeof node.source.value === 'string' &&
      R3F_IMPORT_SOURCES.has(node.source.value),
  );
}

function shouldBailout(name: string): boolean {
  if (R3F_BAILOUT_ELEMENTS.has(name)) {
    return true;
  }

  return R3F_BAILOUT_PATTERNS.some((pattern) => name.match(pattern));
}

/**
 * Walk up the directory tree from `startDir` looking for the closest `package.json`.
 * Returns the absolute path to the directory containing `package.json`, or null if
 * none is found before reaching the filesystem root.
 */
async function findClosestPackageDir(startDir: string): Promise<string | null> {
  let current = path.resolve(startDir);

  // eslint-disable-next-line no-constant-condition -- intentional walk-up loop
  while (true) {
    try {
      await fs.access(path.join(current, 'package.json'));

      return current;
    } catch {
      // package.json not here, move up
    }

    const parent = path.dirname(current);

    if (parent === current) {
      // Reached the filesystem root without finding package.json
      return null;
    }

    current = parent;
  }
}

/**
 * Search for a tailwind config file in the given directory.
 * Returns the absolute file path if found, or null if no config file exists.
 */
async function findTailwindConfigPath(dir: string): Promise<string | null> {
  for (const configFile of TAILWIND_CONFIG_FILES) {
    const configPath = path.join(dir, configFile);

    try {
      await fs.access(configPath);

      return configPath;
    } catch {
      // File doesn't exist, try the next one
    }
  }

  return null;
}

/**
 * Detect the major version of tailwindcss from a `package.json` file.
 * Reads the `tailwindcss` dependency (or devDependency) version string and
 * extracts the major version number. Defaults to 4 if detection fails.
 */
async function detectTailwindMajorVersion(packageDir: string): Promise<number> {
  try {
    const packageJsonPath = path.join(packageDir, 'package.json');
    const packageJsonContent = await fs.readFile(packageJsonPath, 'utf-8');
    const packageJson = JSON.parse(packageJsonContent) as {
      dependencies?: Record<string, string>;
      devDependencies?: Record<string, string>;
    };

    const versionSpec =
      packageJson.dependencies?.tailwindcss ??
      packageJson.devDependencies?.tailwindcss;

    if (!versionSpec) {
      return 4;
    }

    // Strip common range prefixes (^, ~, >=, etc.) and extract the major version
    const match = versionSpec.match(/(\d+)/);

    if (match) {
      return parseInt(match[1], 10);
    }

    return 4;
  } catch {
    // package.json not found or unreadable — default to v4
    return 4;
  }
}

/**
 * Make a tailwind config file browser-safe by converting CJS `require()`
 * calls to ESM `import` statements.
 *
 * Tailwind configs often use `require("tailwindcss-animate")` or similar
 * CJS patterns that don't work in the browser.  This function rewrites
 * each `require("pkg")` into a top-level `import __req_N from "pkg"`
 * and replaces the call-site with the binding identifier.  The actual
 * error handling (in case the imported package is Node-only and crashes)
 * happens at the import site in the entry file — see the `transform`
 * hook where the config is dynamically imported inside a try/catch.
 *
 * @param traverseFn - A resolved `@babel/traverse` default export.
 * Returns `null` if no modifications were needed (config is already ESM-safe).
 */
function makeTailwindConfigBrowserSafe(
  code: string,
  traverseFn: typeof import('@babel/traverse').default,
): { code: string; map: ReturnType<MagicString['generateMap']> } | null {
  const ast = parse(code, {
    sourceType: 'module',
    plugins: ['typescript'],
  });

  const s = new MagicString(code);
  let modified = false;

  // Collect all require() calls and assign each unique module a binding name.
  const importBindings = new Map<string, string>();
  let bindingCounter = 0;

  const requireSites: Array<{
    start: number;
    end: number;
    binding: string;
  }> = [];

  traverseFn(ast, {
    CallExpression(nodePath) {
      const node = nodePath.node;

      if (
        node.callee.type === 'Identifier' &&
        node.callee.name === 'require' &&
        node.arguments.length === 1 &&
        node.arguments[0].type === 'StringLiteral'
      ) {
        if (node.start == null || node.end == null) {
          return;
        }

        const moduleName = node.arguments[0].value;
        let binding = importBindings.get(moduleName);

        if (!binding) {
          binding = `__req_${bindingCounter++}`;
          importBindings.set(moduleName, binding);
        }

        requireSites.push({ start: node.start, end: node.end, binding });
      }
    },
  });

  if (requireSites.length === 0) {
    return null;
  }

  // Build the import statements to prepend
  const importLines: Array<string> = [];

  for (const [moduleName, binding] of importBindings) {
    importLines.push(`import ${binding} from ${JSON.stringify(moduleName)};`);
  }

  s.prepend(importLines.join('\n') + '\n');

  // Replace each require() call-site with its binding
  for (const site of requireSites) {
    s.overwrite(site.start, site.end, site.binding);
    modified = true;
  }

  if (!modified) {
    return null;
  }

  return {
    code: s.toString(),
    map: s.generateMap({ hires: true }),
  };
}

/**
 * Generate the client-side tailwind initialization script.
 *
 * Listens for the `replit-tailwind-config-ready` CustomEvent (dispatched by
 * the injected config loader in the entry file) to know when the config is
 * available.  The beacon dispatches a `replit-init-tailwind` CustomEvent to
 * trigger loading, and this script listens for it.
 *
 * The script includes a built-in once-guard so Tailwind is only loaded once
 * even if the event fires multiple times.
 *
 * **v4** (`@tailwindcss/browser@4`): config is passed as a JSON-serialized `data-config` attribute.
 * **v3** (`cdn.tailwindcss.com`): config is set on `tailwind.config` global via `script.onload`.
 */
function generateClientSideTailwindScript(majorVersion: number): string {
  const cdnUrl =
    majorVersion >= 4
      ? JSON.stringify(TAILWIND_V4_CDN_URL)
      : JSON.stringify(TAILWIND_V3_CDN_URL);

  const loadTailwindBody =
    majorVersion >= 4
      ? `
        var script = document.createElement('script');
        script.src = ${cdnUrl};

        if (window.REPLIT_APP_TAILWIND_CONFIG) {
          script.setAttribute('data-config', JSON.stringify(window.REPLIT_APP_TAILWIND_CONFIG));
        }

        document.head.appendChild(script);`
      : `
        var script = document.createElement('script');
        script.src = ${cdnUrl};

        script.onload = function() {
          if (window.REPLIT_APP_TAILWIND_CONFIG) {
            window.tailwind.config = window.REPLIT_APP_TAILWIND_CONFIG;
          }
        };

        document.head.appendChild(script);`;

  return `
(function() {
  var initialized = false;

  function loadTailwind() {
    if (initialized) { return; }
    initialized = true;
${loadTailwindBody}
  }

  // Listen for the beacon requesting tailwind initialization.
  window.addEventListener('replit-init-tailwind', function() {
    if (window.REPLIT_APP_TAILWIND_CONFIG) {
      loadTailwind();
    } else {
      // Config not ready yet — wait for the config-ready event, but proceed
      // without a config after 1 second.  The config is optional (projects
      // without a tailwind config file will never fire the ready event).
      var configTimeout = setTimeout(function() { loadTailwind(); }, 1000);
      window.addEventListener('replit-tailwind-config-ready', function() {
        clearTimeout(configTimeout);
        loadTailwind();
      }, { once: true });
    }
  });
})();
`.trim();
}
